<script setup lang="ts">
/**
 * The AI 助手's mark, as the admin chooses (AI 助手's settings):
 * - spark: a four-pointed star and a small one, the platform's blue
 *   running to violet; animated, it breathes, turning a little, the small
 *   one twinkles and the colours slowly come round.
 * - pinwheel: four blades in its blues; animated, a turn and a rest.
 * - the mascots — cloud (云宝), bubble (泡泡), cube (方糖, a spark for an
 *   antenna), orb (小球, a spark for a hair clip): animated, their eyes
 *   follow the pointer, blink now and then, and they float a little.
 * busy quickens them while an answer is being written. No motion for
 * those who asked their system for less.
 * The same file in both consoles (the admin's settings show it).
 */
import { computed, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { followPointer, pointer } from '@/utils/pointer'

const props = withDefaults(defineProps<{ icon?: string; size?: number; animated?: boolean; busy?: boolean }>(), {
  icon: 'spark',
  size: 24,
  animated: false,
  busy: false,
})
const id = `assistant-ink-${useId()}`
const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
const moving = computed(() => props.animated && !reduced)

// A four-pointed star: k is how full its sides are.
function spark(cx: number, cy: number, r: number, k: number) {
  const c = r * k
  return `M${cx} ${cy - r}Q${cx + c} ${cy - c} ${cx + r} ${cy}Q${cx + c} ${cy + c} ${cx} ${cy + r}Q${cx - c} ${cy + c} ${cx - r} ${cy}Q${cx - c} ${cy - c} ${cx} ${cy - r}Z`
}
const STAR = spark(14.2, 17.6, 12.4, 0.26)
const STAR_SMALL = spark(25.2, 7, 4.4, 0.3)
const BLADE = 'M16 16C15.2 10.6 17.6 5.6 23.4 4.6C24.4 10.4 21.4 14.6 16 16Z'
const BLADES = [
  { color: '#2f6bff', turn: 0 },
  { color: '#5a5cff', turn: 90 },
  { color: '#8a5cff', turn: 180 },
  { color: '#3fb6ff', turn: 270 },
]

// The mascots: where each one's eyes sit, and its spark if it has one.
const MASCOTS: Record<string, { eyes: { y: number; x: [number, number] }; accent?: string }> = {
  cloud: { eyes: { y: 18.8, x: [12.4, 19.6] }, accent: spark(27, 5.4, 2.8, 0.3) },
  bubble: { eyes: { y: 14.6, x: [12.5, 19.5] } },
  cube: { eyes: { y: 18.4, x: [12.5, 19.5] }, accent: spark(16, 3.8, 2.9, 0.3) },
  orb: { eyes: { y: 17.4, x: [12.8, 19.2] }, accent: spark(23.6, 4.8, 3.2, 0.3) },
}
const mascot = computed(() => MASCOTS[props.icon])

// A mascot looks toward the pointer: up to 2.6 units across and 1.9 up or
// down, more the farther it is, straight ahead when it is off the page.
const el = ref<SVGSVGElement>()
let unfollow: (() => void) | null = null
watch(
  () => !!mascot.value && moving.value,
  (on) => {
    if (on && !unfollow) unfollow = followPointer()
    else if (!on && unfollow) {
      unfollow()
      unfollow = null
    }
  },
)
onMounted(() => {
  if (mascot.value && moving.value) unfollow = followPointer()
})
onBeforeUnmount(() => unfollow?.())
const look = computed(() => {
  if (!mascot.value || !moving.value || !pointer.active || !el.value) return { x: 0, y: 0 }
  const r = el.value.getBoundingClientRect()
  const dx = pointer.x - (r.left + r.width / 2)
  const dy = pointer.y - (r.top + r.height / 2)
  const d = Math.hypot(dx, dy) || 1
  const reach = Math.min(1, d / 90)
  return { x: (dx / d) * 2.6 * reach, y: (dy / d) * 1.9 * reach }
})
</script>

<template>
  <svg
    ref="el"
    class="assistant-mark"
    :class="[mascot ? 'mascot' : icon === 'pinwheel' ? 'pinwheel' : 'spark', { moving, busy: moving && busy }]"
    :width="size"
    :height="size"
    viewBox="0 0 32 32"
    aria-hidden="true"
  >
    <g v-if="icon === 'pinwheel'" class="wheel">
      <path v-for="b in BLADES" :key="b.turn" :d="BLADE" :fill="b.color" :transform="b.turn ? `rotate(${b.turn} 16 16)` : undefined" />
    </g>
    <template v-else-if="mascot">
      <defs>
        <linearGradient :id="id" x1="4" y1="3" x2="27" y2="30" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#4b8bff" />
          <stop offset="0.5" stop-color="#6d5dff" />
          <stop offset="1" stop-color="#a65cf2" />
        </linearGradient>
      </defs>
      <g class="body">
        <path
          v-if="icon === 'cloud'"
          :fill="`url(#${id})`"
          d="M8.6 27.2h14.8a6.6 6.6 0 0 0 .95-13.13A8.7 8.7 0 0 0 7.65 12.6a7.3 7.3 0 0 0 .95 14.6z"
        />
        <path
          v-else-if="icon === 'bubble'"
          :fill="`url(#${id})`"
          d="M10.2 4.4h11.6a7.2 7.2 0 0 1 7.2 7.2v6.4a7.2 7.2 0 0 1-7.2 7.2h-6.4l-4.9 3.9c-.62.5-1.5.05-1.5-.74V24.9A7.2 7.2 0 0 1 3 18V11.6a7.2 7.2 0 0 1 7.2-7.2z"
        />
        <template v-else-if="icon === 'cube'">
          <line x1="16" y1="9.4" x2="16" y2="6.2" :stroke="`url(#${id})`" stroke-width="1.8" stroke-linecap="round" />
          <rect x="4.5" y="9" width="23" height="19.6" rx="7.5" :fill="`url(#${id})`" />
        </template>
        <circle v-else cx="16" cy="17.6" r="11.8" :fill="`url(#${id})`" />
        <path v-if="mascot.accent" class="accent" :fill="`url(#${id})`" :d="mascot.accent" />
        <g class="eyes" :style="{ transform: `translate(${look.x}px, ${look.y}px)` }">
          <rect class="eye" :x="mascot.eyes.x[0] - 1.5" :y="mascot.eyes.y - 2.6" width="3" height="5.2" rx="1.5" fill="#fff" />
          <rect class="eye" :x="mascot.eyes.x[1] - 1.5" :y="mascot.eyes.y - 2.6" width="3" height="5.2" rx="1.5" fill="#fff" />
        </g>
      </g>
    </template>
    <template v-else>
      <defs>
        <linearGradient :id="id" x1="3" y1="3" x2="29" y2="29" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#2f6bff" />
          <stop offset="0.55" stop-color="#6f5cff" />
          <stop offset="1" stop-color="#b25cf0" />
          <animateTransform
            v-if="moving"
            attributeName="gradientTransform"
            type="rotate"
            from="0 16 16"
            to="360 16 16"
            :dur="busy ? '3s' : '9s'"
            repeatCount="indefinite"
          />
        </linearGradient>
      </defs>
      <path class="star" :fill="`url(#${id})`" :d="STAR" />
      <path class="star-small" :fill="`url(#${id})`" :d="STAR_SMALL" />
    </template>
  </svg>
</template>

<style scoped>
.assistant-mark {
  display: block;
  flex: none;
  overflow: visible;
}
/* Each part turns about its own centre, in the mark's 32-unit square. */
.star,
.star-small,
.wheel {
  transform-box: view-box;
}
.star {
  transform-origin: 14.2px 17.6px;
}
.star-small {
  transform-origin: 25.2px 7px;
}
.wheel {
  transform-origin: 16px 16px;
}
.eye,
.accent,
.body {
  transform-box: fill-box;
  transform-origin: center;
}
.eyes {
  transition: transform 0.12s ease-out;
}
.moving .star {
  animation: assistant-breathe 3.2s ease-in-out infinite;
}
.moving .star-small {
  animation: assistant-twinkle 3.2s ease-in-out infinite;
}
.moving .wheel {
  animation: assistant-turn 4s cubic-bezier(0.45, 0, 0.2, 1) infinite;
}
.moving .eye {
  animation: assistant-blink 4.6s infinite;
}
.moving .accent {
  animation: assistant-glint 3.2s ease-in-out infinite;
}
.moving .body {
  animation: assistant-float 3.6s ease-in-out infinite;
}
.busy .star,
.busy .star-small,
.busy .accent {
  animation-duration: 1.2s;
}
.busy .body {
  animation-duration: 1.4s;
}
.busy .wheel {
  animation: assistant-spin 0.9s linear infinite;
}
@keyframes assistant-breathe {
  0%,
  100% {
    transform: scale(1) rotate(0deg);
  }
  50% {
    transform: scale(0.84) rotate(15deg);
  }
}
@keyframes assistant-twinkle {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  45%,
  55% {
    transform: scale(0.35);
    opacity: 0.35;
  }
}
/* A turn, then a rest. */
@keyframes assistant-turn {
  0% {
    transform: rotate(0deg);
  }
  40%,
  100% {
    transform: rotate(360deg);
  }
}
@keyframes assistant-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes assistant-blink {
  0%,
  92%,
  100% {
    transform: scaleY(1);
  }
  95% {
    transform: scaleY(0.12);
  }
}
@keyframes assistant-glint {
  0%,
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(0.6) rotate(45deg);
    opacity: 0.6;
  }
}
@keyframes assistant-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-0.8px);
  }
}
</style>
